import { create } from 'zustand';

/**
 * Global "unsaved changes" navigation guard.
 *
 * Some surfaces hold in-progress form edits that must not be lost to a stray
 * navigation — currently the patient Add/Edit page. In Flutter PM the
 * create/edit patient screen is a PUSHED route with the side menu hidden, so
 * its own Back button (`doCancel` → `cancelCreatingDialog`,
 * create_patient_screen.dart:249-293) is the ONLY exit and the only thing that
 * needs guarding. The React shell keeps the SideNav and profile menu mounted
 * alongside the edit page, so it exposes extra exit paths Flutter never had —
 * clicking Calendar / Billing / Logout would silently discard the edits. This
 * store lets those shell-level nav triggers defer to the same confirm the
 * in-page Back button already shows.
 *
 * Lives in `shared/` because the app shell (`App.tsx`, `ProfilePopover`) and
 * the `patients/` domain both use it, and neither may import the other
 * (CLAUDE.md structural rule).
 *
 * Contract:
 *   - The dirty surface calls `setBlocking(isDirty)` and clears it on unmount.
 *   - A shell nav trigger wraps its navigation in `run(proceed)`: if nothing
 *     is blocking, `proceed` fires immediately (zero behaviour change off the
 *     edit page); if blocked, `proceed` is stashed as `pending` and the global
 *     `<UnsavedChangesGuardDialog>` opens.
 *   - The dialog calls `confirm()` (discard → run + clear the pending nav) or
 *     `cancel()` (keep editing → drop the pending nav).
 */
interface NavGuardState {
  /** True while a mounted surface has unsaved edits that should block exit. */
  blocking: boolean;
  /** The deferred navigation awaiting a discard confirmation, or null. */
  pending: (() => void) | null;
  setBlocking: (blocking: boolean) => void;
  /** Run `proceed` now if unblocked, else stash it and open the confirm. */
  run: (proceed: () => void) => void;
  /** Discard: clear the block and run the stashed navigation. */
  confirm: () => void;
  /** Keep editing: drop the stashed navigation. */
  cancel: () => void;
}

export const useNavGuard = create<NavGuardState>((set, get) => ({
  blocking: false,
  pending: null,
  setBlocking: (blocking) => set({ blocking }),
  run: (proceed) => {
    if (!get().blocking) {
      proceed();
      return;
    }
    set({ pending: proceed });
  },
  confirm: () => {
    const { pending } = get();
    // Clear the block BEFORE running the nav: `proceed` unmounts the dirty
    // surface, whose cleanup would otherwise re-assert setBlocking(false)
    // anyway — clearing here keeps the store consistent if `proceed` is sync.
    set({ blocking: false, pending: null });
    pending?.();
  },
  cancel: () => set({ pending: null }),
}));
